import React, { useContext, useEffect, useMemo, useState } from 'react';

// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { createEditorContentUnifiedQueryVariables } from '@confluence/editor-content-unified-query/entry-points/createEditorContentUnifiedQueryVariables.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { useNativeCollabState } from '@confluence/native-collab/entry-points/useNativeCollabState.ts';

import { getContentAppearanceFromQueryData, contentAppearanceQuery } from './contentAppearance.ts';
import type { ContentAppearance, EditorWidthMetadata } from './contentAppearanceTypes.ts';
import { ContentAppearancePropertyKey, ContentAppearanceType } from './contentAppearanceTypes.ts';
import { useContentAppearanceFragment } from './useContentAppearanceFragment.ts';
import type {
	contentAppearanceQuery$data,
	contentAppearanceQuery$variables,
} from './__generated__/contentAppearanceQuery.graphql.ts';
type ContentAppearanceContextType = {
	contentAppearance: ContentAppearance;
	setContentAppearance(contentAppearance: ContentAppearance): void;
	contentAppearanceLoading: boolean;
};
const ContentAppearanceContext = React.createContext<ContentAppearanceContextType>({
	contentAppearance: {
		appearance: ContentAppearanceType.DEFAULT,
		version: 0,
	},
	setContentAppearance: () => {},
	contentAppearanceLoading: false,
});

type ContentAppearanceContextProviderProps = {
	contentId: string;
	children: React.ReactNode;
};
export const ContentAppearanceContextProvider = ({
	contentId,
	children,
}: ContentAppearanceContextProviderProps): React.JSX.Element => {
	const [contentAppearanceOverride, setContentAppearanceOverride] = useState<ContentAppearance>();

	// Clear the override state when navigating to a different page
	useEffect(() => {
		setContentAppearanceOverride(undefined);
	}, [contentId]);

	const [{ collabEditProvider }] = useNativeCollabState();
	// Subscribe to metadata:changed events to update the content appearance for collaborators
	useEffect(() => {
		if (!collabEditProvider) {
			return;
		}

		const handleMetadataChanged = (metadata: Record<string, unknown>) => {
			const editorWidth = metadata?.editorWidth as EditorWidthMetadata | undefined;
			const widthValue = editorWidth?.value;
			const version = editorWidth?.version;

			if (widthValue && version !== undefined) {
				setContentAppearanceOverride((currentAppearance) => {
					if (!currentAppearance || version > currentAppearance.version) {
						return {
							appearance: widthValue,
							version,
						};
					} else {
						return currentAppearance;
					}
				});
			}
		};

		collabEditProvider.on('metadata:changed', handleMetadataChanged);
		return () => {
			collabEditProvider.off('metadata:changed', handleMetadataChanged);
		};
	}, [collabEditProvider]);

	/*
		Previous check was:
		const draftAppearanceQuery = getContentAppearanceQuery(ContentAppearancePropertyKey.DRAFT);
		which always returns contentAppearanceQuery
	*/
	const contentAppearanceFragmentResult = useContentAppearanceFragment({ contentId });
	const contentAppearanceQueryResult = useQueryInterop<
		contentAppearanceQuery$data,
		contentAppearanceQuery$variables
	>(contentAppearanceQuery, {
		variables: expValEquals('confluence_editor_content_unified_query', 'isEnabled', true)
			? createEditorContentUnifiedQueryVariables({ contentId })
			: {
					contentId,
					status: ['current', 'draft'],
				},
		skip: contentAppearanceFragmentResult.isProvided,
	});
	// The query error is not read here, so a field error only reads as no data.
	const { data: initialContentAppearanceData, loading: initialContentAppearanceLoading } =
		contentAppearanceFragmentResult.isProvided
			? contentAppearanceFragmentResult
			: contentAppearanceQueryResult;

	const context = useMemo(() => {
		const initialContentAppearance = getContentAppearanceFromQueryData(
			initialContentAppearanceData,
			ContentAppearancePropertyKey.DRAFT,
		);

		// Under certain circumstances due to a previous bug with the content appearance during legacy editor autoconvert, an incorrect value may have been sent to collab service.
		// In this circumstance favour the initial content appearance value.
		const shouldUseInitial =
			contentAppearanceOverride &&
			initialContentAppearance &&
			contentAppearanceOverride.version === initialContentAppearance.version &&
			contentAppearanceOverride.appearance !== initialContentAppearance.appearance;

		return {
			contentAppearance: shouldUseInitial
				? initialContentAppearance
				: contentAppearanceOverride || initialContentAppearance,
			setContentAppearance: setContentAppearanceOverride,
			contentAppearanceLoading: initialContentAppearanceLoading,
		};
	}, [initialContentAppearanceData, initialContentAppearanceLoading, contentAppearanceOverride]);

	return (
		<ContentAppearanceContext.Provider value={context}>
			{children}
		</ContentAppearanceContext.Provider>
	);
};

type ContentAppearanceContextConsumerProps = {
	children: (props: ContentAppearanceContextType) => JSX.Element;
};
export const ContentAppearanceContextConsumer = ({
	children,
}: ContentAppearanceContextConsumerProps): JSX.Element => {
	const context = useContentAppearance();
	return children(context);
};

export const useContentAppearance = (): ContentAppearanceContextType => {
	return useContext(ContentAppearanceContext);
};
